import React, { useState, useEffect } from 'react'; 

import { handleResponse } from '../../../../_helpers/handle-response';
import { DropdownDataService } from '../../../../_services/dropdownData.service';
import CONSTANTS from '../../../../_constants/constants';
import { parseFloat2Decimal, parseFloat2DecimalHeader } from '../../../../_helpers/utils';
import classes from './SupplierWiseProfitPage.module.css';

import Alert from '../../../UI/Alert/Alert';
import Button from '../../../UI/Button/Button';
import Loader from '../../../UI/Loader/Loader';
import Table from '../../../UI/Table/Table';
import ExportToCSV from '../../../UI/ExportToCSV/ExportToCSV';
import PageLayout from '../../../Layout/PageLayout/PageLayout';

const SupplierWiseProfitPage = () => {

    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [fromDate, setFromDate] = useState(new Date().toISOString().slice(0,10));
    const [toDate, setToDate] = useState(new Date().toISOString().slice(0,10));
    const [supplierId, setSupplierId] = useState('');
    const [operatorId, setOperatorId] = useState('');
    const [supplierData, setSupplierData] = useState([]);
    const [operatorData, setOperatorData] = useState([]);
    const [gridData, setGridData] = useState([]);
    const [totalData, setTotalData] = useState([]);
    const [pageCount, setPageCount] = useState(1);
    const [exportFileName, setExportFileName] = useState('');
    const [enableExport, setEnableExport] = useState(false);
    const [exportTotalData, setExportTotalData] = useState({});
    
    const tableHeaders = ['Supplier', 'Sales', 'Commission Received', 'Commission Paid', 'Commission Margin', 'Incentive', 'Net Profit'];
    const dataFields = ['supplierName', 'sales', 'commissionReceived', 'commissionPaid', 'commissionMargin', 'incentive', 'netProfit'];
    const csvHeaders = [
        { label: 'Supplier', key: 'supplierName' },
        { label: 'Sales', key: 'sales' },
        { label: 'Commission Received', key: 'commissionReceived' },
        { label: 'Commission Paid', key: 'commissionPaid' },
        { label: 'Commission Margin', key: 'commissionMargin' },
        { label: 'Incentive', key: 'incentive' },
        { label: 'Net Profit', key: 'netProfit' }
    ];
    const tableConfig = {
        headerAlignConfig: {
            'Supplier': 'left', 
            'Sales': 'center', 
            'Commission Received': 'center', 
            'Commission Paid': 'center', 
            'Commission Margin': 'center', 
            'Incentive': 'center', 
            'Net Profit': 'center'
        },
        widthConfig: {
            'Supplier': 'auto', 
            'Sales': '154px', 
            'Commission Received': '162px', 
            'Commission Paid': '162px', 
            'Commission Margin': '162px', 
            'Incentive': '162px', 
            'Net Profit': '162px'
        },
        alignConfig: {
            'supplierName': 'left',
            'sales': 'right',
            'commissionReceived': 'right',
            'commissionPaid': 'right', 
            'commissionMargin': 'right',
            'incentive' : 'right',
            'netProfit': 'right'
        }
    };

    useEffect(() => {
        getSupplierList();
        getOperatorList();
    }, []);

    useEffect(() => {
        setEnableExport(false);
        setErrorMessage(null);
    }, [fromDate, toDate, supplierId, operatorId]);

    const getSupplierList = () => {
        DropdownDataService.getSupplierList('filter').then( result => {
            if(result.data) {
                setSupplierId(result.data.results[0].supplierId);
                setSupplierData(result.data.results);
            } else {
                setErrorMessage(result);
            }
        });
    }

    const getOperatorList = () => {
        DropdownDataService.getOperatorList('filter').then( result => {
            if(result.data) {
                setOperatorId(result.data.results[0].operatorId);
                setOperatorData(result.data.results);
            } else {
                setErrorMessage(result);
            }
        });
    }

    const getReportData = () => {
        const requestOptions = {
            headers: { 
                'Token': localStorage.getItem('accessToken'),
                'Content-Type': 'application/json'
            },
        };
        let url = '';
        url = (supplierId === '-1' && operatorId === '-1') ?
                `${CONSTANTS.API_URL}Reports/SupplierwiseProfitReport?startDate=${fromDate}&endDate=${toDate}` :
                supplierId === '-1' ?
                    `${CONSTANTS.API_URL}Reports/SupplierwiseProfitReport?startDate=${fromDate}&endDate=${toDate}&operatorId=${operatorId}` :
                    operatorId === '-1' ?
                        `${CONSTANTS.API_URL}Reports/SupplierwiseProfitReport?startDate=${fromDate}&endDate=${toDate}&supplierId=${supplierId}` :
                        `${CONSTANTS.API_URL}Reports/SupplierwiseProfitReport?startDate=${fromDate}&endDate=${toDate}&supplierId=${supplierId}&operatorId=${operatorId}`
        fetch(url, requestOptions)
        .then(handleResponse)
        .then(
            (result) => {
                if(result.data) {
                    setIsLoading(false);
                    const keysToParse = ['sales', 'commissionReceived', 'commissionPaid', 'commissionMargin', 'incentive', 'netProfit'];
                    parseFloat2Decimal(result.data.results, keysToParse);
                    setGridData(result.data.results);
                    setPageCount(Math.ceil(result.data.results.length/10));
                    let total = result.data.header;
                    const headerKeysToParse = ['sales', 'commissionReceived', 'commissionPaid', 'commissionMargin', 'incentive', 'netProfit'];
                    parseFloat2DecimalHeader(total, headerKeysToParse);
                    setTotalData([
                        total.sales,
                        total.commissionReceived,
                        total.commissionPaid,
                        total.commissionMargin,
                        total.incentive,
                        total.netProfit
                    ]);
                    setExportTotalData({
                        supplierName: '',
                        sales: total.sales,
                        commissionReceived: total.commissionReceived,
                        commissionPaid: total.commissionPaid,
                        commissionMargin: total.commissionMargin,
                        incentive: total.incentive,
                        netProfit: total.netProfit
                    });
                    setEnableExport(result.data.results.length > 0 ? true : false);
                } else {
                    setIsLoading(false);
                    setErrorMessage(result.error.message);
                }
            },
            error => {
                setIsLoading(false);
                setErrorMessage(error);
            }
        );
    }

    const showClickHandler = () => {
        setIsLoading(true);
        getReportData();
    }

    const fromDateChangeHandler = event => setFromDate(event.target.value)
    
    const toDateChangeHandler = event => setToDate(event.target.value)

    const supplierChangeHandler = event => setSupplierId(event.target.value)
    
    const operatorChangeHandler = event => setOperatorId(event.target.value)

    const getExportFileName = () => {
        let exportFilename = 'SupplierWiseProfit';
        exportFilename += '_' + fromDate;
        exportFilename += '_' + toDate;
        exportFilename += supplierId !== '-1' ? '_' + supplierData.find(data => data.supplierId == supplierId)['supplierName'] : '';
        exportFilename += operatorId !== '-1' ? '_' + operatorData.find(data => data.operatorId == operatorId)['operatorName'] : '';
        setExportFileName(exportFilename);
    }

    return (
        <>
            { isLoading && <Loader />}
            { !isLoading && (
                <PageLayout pageTitle='Supplier Wise Profit'>
                    <div className='row mb-1'>
                        <div className='col-12' style={{display: 'flex', justifyContent:'space-between', marginTop: '6px', marginBottom: '6px'}}>
                            <form className='form-inline'>
                                <div className={`form-group ${classes.inputContainer}`}>
                                    <label className='col-form-label inputLabel'>From Date : </label>
                                    <input type='date' onKeyDown={(e) => e.preventDefault()} required className='form-control inputField' value={fromDate} onChange={fromDateChangeHandler} />
                                </div>
                                <div className={`form-group ${classes.inputContainer}`}>
                                    <label className='col-form-label inputLabel'>To Date : </label>
                                    <input type='date' onKeyDown={(e) => e.preventDefault()} required className='form-control inputField' value={toDate} onChange={toDateChangeHandler} />
                                </div>
                                <div className={`form-group ${classes.inputContainer}`}>
                                    <label className='col-form-label inputLabel'>Supplier : </label>
                                    <select className='form-select customSelect' value={supplierId} aria-label=".form-select-sm example" onChange={supplierChangeHandler}>
                                        { supplierData.map( (value, index) => {
                                            return <option key={`supplier-${index}`} className='dropdownItem' value={value.supplierId}>{value.supplierName}</option>
                                        }) }
                                    </select>
                                </div>
                                <div className={`form-group ${classes.inputContainer}`}>
                                    <label className='col-form-label inputLabel'>Operator : </label>
                                    <select className='form-select customSelect' value={operatorId} aria-label=".form-select-sm example" onChange={operatorChangeHandler}>
                                        { operatorData.map( (value, index) => {
                                            return <option key={`operator-${index}`} className='dropdownItem' value={value.operatorId}>{value.operatorName}</option>
                                        }) }
                                    </select>
                                </div>
                            </form>
                            <div>
                                <Button btnClass='btn btn-primary mr-8' onClick={showClickHandler} btnText='Show' />
                                <ExportToCSV 
                                    headers={csvHeaders}
                                    exportTotalData={exportTotalData}
                                    gridData={gridData}
                                    exportFileName={exportFileName} 
                                    exportClickHandler={getExportFileName} 
                                    enableExport={enableExport}
                                />
                            </div>
                        </div>
                    </div>
                    { errorMessage && <Alert alertClassName='danger' message={errorMessage} /> }
                    <Table 
                        tableHeaders={tableHeaders} 
                        dataFields={dataFields} 
                        tableConfig={tableConfig}
                        pageCount={pageCount} 
                        sort={false}
                        actions={false} 
                        view={false}
                        paginate={true}
                        filter={false}
                        gridData={gridData}
                        totalData={totalData}
                    />
                </PageLayout>
            )}    
        </>
    );
  
}

export default SupplierWiseProfitPage;